import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Euro as EuroIcon,
    LocalOffer as PromotionIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import {
    getPromoCodes,
    createPromoCode,
    updatePromoCode,
    deletePromoCode
} from '../Utils/pricingApi';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const emptyPromoForm = {
    code: '', pricing_plan_id: '', discount_type: 'percentage', discount_value: '', is_active: true
};

const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>{value === index && <Box sx={{ py: 3 }}>{children}</Box>}</div>
);

const PricingPlansListPage = () => {
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const canManagePromoCodes = userInfo?.is_super_admin === true || (userInfo?.permissions || []).includes('pricing-plans.promo-codes.manage');
    const authHeaders = { Authorization: `Bearer ${accessToken}` };

    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const [applications, setApplications] = useState([]);
    const [loadingApps, setLoadingApps] = useState(true);

    const [promoCodes, setPromoCodes] = useState([]);
    const [loadingPromos, setLoadingPromos] = useState(true);
    const [promoError, setPromoError] = useState(null);
    const [promoDialogOpen, setPromoDialogOpen] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);
    const [promoForm, setPromoForm] = useState(emptyPromoForm);
    const [promoFormError, setPromoFormError] = useState('');

    useEffect(() => {
        if (!accessToken) return;
        setLoadingApps(true);
        axios.get(`${API_URL}/api/applications`, { headers: authHeaders })
            .then(res => setApplications(Array.isArray(res.data) ? res.data : []))
            .catch(() => setApplications([]))
            .finally(() => setLoadingApps(false));
    }, [accessToken]);

    const fetchPromoCodes = useCallback(() => {
        if (!accessToken) return;
        setLoadingPromos(true);
        getPromoCodes(accessToken)
            .then(setPromoCodes)
            .catch(() => setPromoError('Impossible de charger les codes promo'))
            .finally(() => setLoadingPromos(false));
    }, [accessToken]);

    useEffect(() => { fetchPromoCodes(); }, [fetchPromoCodes]);

    const filteredApps = applications.filter(app =>
        app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.client_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const openCreatePromo = () => {
        setEditingPromo(null);
        setPromoForm(emptyPromoForm);
        setPromoFormError('');
        setPromoDialogOpen(true);
    };

    const openEditPromo = (promo) => {
        setEditingPromo(promo);
        setPromoForm({
            code: promo.code,
            pricing_plan_id: promo.pricing_plan_id ?? '',
            discount_type: promo.discount_type,
            discount_value: promo.discount_value,
            is_active: promo.is_active
        });
        setPromoFormError('');
        setPromoDialogOpen(true);
    };

    const handleSavePromo = () => {
        const payload = {
            code: promoForm.code.trim().toUpperCase(),
            pricing_plan_id: promoForm.pricing_plan_id === '' ? null : parseInt(promoForm.pricing_plan_id, 10),
            discount_type: promoForm.discount_type,
            discount_value: parseFloat(promoForm.discount_value) || 0,
            is_active: promoForm.is_active
        };

        const req = editingPromo
            ? updatePromoCode(accessToken, editingPromo.id, payload)
            : createPromoCode(accessToken, payload);

        req.then(() => { setPromoDialogOpen(false); fetchPromoCodes(); })
           .catch(err => setPromoFormError(err.response?.data?.message || 'Erreur lors de l\'enregistrement'));
    };

    const handleDeletePromo = (promo) => {
        if (!window.confirm(`Supprimer le code promo "${promo.code}" ?`)) return;
        deletePromoCode(accessToken, promo.id).then(fetchPromoCodes).catch(() => setPromoError('Suppression impossible'));
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EuroIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        Gestion des prix
                    </Typography>
                </Box>
            </Box>

            <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                        <Tab label="Vue d'ensemble" icon={<AssignmentIcon />} iconPosition="start" />
                        <Tab label="Codes promo" icon={<PromotionIcon />} iconPosition="start" />
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    <Box sx={{ px: { xs: 2, sm: 3 } }}>
                        <TextField
                            fullWidth
                            placeholder="Rechercher une application..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                            }}
                        />
                        {loadingApps ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Application</TableCell>
                                            <TableCell>Catégorie</TableCell>
                                            <TableCell>Statut</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredApps.map((app) => (
                                            <TableRow key={app.client_id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{app.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{app.client_id}</Typography>
                                                </TableCell>
                                                <TableCell>{app.category || '—'}</TableCell>
                                                <TableCell>
                                                    <Chip label={app.isActive ? 'Actif' : 'Inactif'} size="small"
                                                        color={app.isActive ? 'success' : 'default'} />
                                                </TableCell>
                                                <TableCell>
                                                    <Button size="small" startIcon={<EuroIcon />}
                                                        onClick={() => navigate(`/applications/${app.client_id}/pricing`)}>
                                                        Gérer la tarification
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Box sx={{ px: { xs: 2, sm: 3 } }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Vue globale de tous les codes promo. Pour créer un code lié à un plan tarifaire précis,
                            renseigne son identifiant, ou fais-le directement depuis la page de l'application concernée.
                        </Alert>
                        {promoError && <Alert severity="error" sx={{ mb: 2 }}>{promoError}</Alert>}
                        {canManagePromoCodes && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePromo}
                                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                                    Nouveau code promo
                                </Button>
                            </Box>
                        )}
                        {loadingPromos ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                        ) : promoCodes.length === 0 ? (
                            <Alert severity="info">Aucun code promo pour le moment.</Alert>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Code</TableCell>
                                            <TableCell>Remise</TableCell>
                                            <TableCell>Plan lié</TableCell>
                                            <TableCell>Usage</TableCell>
                                            <TableCell>Statut</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {promoCodes.map((promo) => (
                                            <TableRow key={promo.id} hover>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{promo.code}</TableCell>
                                                <TableCell>
                                                    {promo.discount_type === 'percentage' ? `-${promo.discount_value}%` : `-${promo.discount_value}`}
                                                </TableCell>
                                                <TableCell>{promo.pricing_plan_id ?? 'Global'}</TableCell>
                                                <TableCell>{promo.used_count}{promo.max_uses ? ` / ${promo.max_uses}` : ''}</TableCell>
                                                <TableCell>
                                                    <Chip label={promo.is_active ? 'Actif' : 'Inactif'} size="small"
                                                        color={promo.is_active ? 'success' : 'default'} />
                                                </TableCell>
                                                <TableCell>
                                                    {canManagePromoCodes && (
                                                        <>
                                                            <IconButton size="small" onClick={() => openEditPromo(promo)}><EditIcon fontSize="small" /></IconButton>
                                                            <IconButton size="small" color="error" onClick={() => handleDeletePromo(promo)}><DeleteIcon fontSize="small" /></IconButton>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </TabPanel>
            </Card>

            <Dialog open={promoDialogOpen} onClose={() => setPromoDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPromo ? 'Modifier le code promo' : 'Nouveau code promo'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {promoFormError && <Grid item xs={12}><Alert severity="error">{promoFormError}</Alert></Grid>}
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Code" value={promoForm.code}
                                onChange={(e) => setPromoForm(f => ({ ...f, code: e.target.value }))} disabled={!!editingPromo} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="ID du plan tarifaire (optionnel, vide = code global)"
                                type="number" value={promoForm.pricing_plan_id}
                                onChange={(e) => setPromoForm(f => ({ ...f, pricing_plan_id: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type de remise</InputLabel>
                                <Select value={promoForm.discount_type} label="Type de remise"
                                    onChange={(e) => setPromoForm(f => ({ ...f, discount_type: e.target.value }))}>
                                    <MenuItem value="percentage">Pourcentage</MenuItem>
                                    <MenuItem value="fixed_amount">Montant fixe</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Valeur" type="number" value={promoForm.discount_value}
                                onChange={(e) => setPromoForm(f => ({ ...f, discount_value: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={promoForm.is_active}
                                    onChange={(e) => setPromoForm(f => ({ ...f, is_active: e.target.checked }))} />}
                                label="Code actif"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPromoDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSavePromo} disabled={!promoForm.code || promoForm.discount_value === ''}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingPromo ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PricingPlansListPage;
